import React from "react";
import { Button, Text, tokens } from "@fluentui/react-components";

export interface CitationSource {
  id: string;
  subject: string;
  from: string;
}

interface CitationsProps {
  sources: CitationSource[];
  /** Opens the source email in a reading window when its subject is clicked.
   * Omitted → subjects render as static text (no behavior change). */
  onOpen?: (id: string) => void;
}

/**
 * Renders the "Sources" list under an assistant answer: the emails actually read
 * (via `get_email_body`) while producing that answer, deterministically traced by
 * `runAgentLoop` — see `docs/superpowers/plans/2026-07-02-gap-3-streaming-citations-richtext.md`.
 * Renders nothing when there are no sources, so turns that didn't read any email
 * don't grow an empty block.
 */
export function Citations({ sources, onOpen }: CitationsProps) {
  if (sources.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        maxWidth: "85%",
        alignSelf: "flex-start",
        padding: "2px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Text size={100} weight="semibold" style={{ color: tokens.colorNeutralForeground3 }}>
        Sources
      </Text>
      {sources.map(source => {
        const label = source.from ? `${source.subject} — ${source.from}` : source.subject;
        if (!onOpen) {
          return (
            <Text key={source.id} size={100} style={{ color: tokens.colorNeutralForeground3 }}>
              {label}
            </Text>
          );
        }
        return (
          <Button
            key={source.id}
            appearance="transparent"
            size="small"
            onClick={() => onOpen(source.id)}
            style={{
              height: "auto",
              minHeight: 0,
              padding: 0,
              justifyContent: "flex-start",
              fontSize: tokens.fontSizeBase100,
              fontWeight: tokens.fontWeightRegular,
              color: tokens.colorNeutralForeground3,
            }}
          >
            {label}
          </Button>
        );
      })}
    </div>
  );
}
